import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  fetchSupplierPayments,
  recordSupplierPayment,
} from '@/renderer/services/inventorySupplierPaymentService';
import type {
  InventorySupplierPaymentFilterParams,
  RecordSupplierPaymentRequest,
} from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useInventorySupplierPayments(
  params: InventorySupplierPaymentFilterParams,
) {
  return useQuery({
    queryKey: queryKeys.inventory.supplierPaymentsList(params),
    queryFn: async () => {
      const result = await fetchSupplierPayments(params);
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to fetch supplier payments');
      }
      return result.data;
    },
    enabled: !!params.companyId && !!params.outletId,
    placeholderData: keepPreviousData,
  });
}

export function useRecordSupplierPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RecordSupplierPaymentRequest) =>
      recordSupplierPayment(request),
    onSuccess: (result) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to record payment');
        return;
      }
      apiSuccess('Payment recorded — accounts payable updated');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-payments-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoices-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoice-summary'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoice-detail'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to record payment');
    },
  });
}
